/**
 * Presentation helpers shared by the pages. Pure functions — no React, no API.
 */

import type { RenewalPeriod } from '@subs/shared';

const MONEY_INTEGER = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const MONEY_TWO = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** `1440` -> `1 440`, `1440.5` -> `1 440,50`. */
export function formatAmount(value: number, currency?: string): string {
  const digits = Math.abs(value % 1) > 0.0001 ? 2 : 0;
  const formatted = (digits === 0 ? MONEY_INTEGER : MONEY_TWO).format(value);
  return currency ? `${formatted} ${currency}` : formatted;
}

/** Symbol for the currencies this system actually carries. */
export function currencySymbol(currency: string): string {
  if (currency === 'RUB') return '₽';
  if (currency === 'USD') return '$';
  if (currency === 'EUR') return '€';
  return `${currency} `;
}

/** Compact form for tight matrix cells: `$144` / `₽11 740` / `KZT 1 440`. */
export function formatCompact(value: number, currency: string): string {
  const symbol = currencySymbol(currency);
  const formatted = MONEY_INTEGER.format(value);
  return symbol.endsWith(' ') ? `${symbol}${formatted}` : `${symbol}${formatted}`;
}

/** `2026-08-07T16:49:00.000Z` -> `07.08.2026`. */
export function formatDate(iso: string | Date): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(date.getTime())) return '—';
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${date.getUTCFullYear()}`;
}

/** `07.08.2026 16:49` in UTC (the dataset's timestamps are UTC bank times). */
export function formatDateTime(iso: string | Date): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(date.getTime())) return '—';
  const hh = String(date.getUTCHours()).padStart(2, '0');
  const mi = String(date.getUTCMinutes()).padStart(2, '0');
  return `${formatDate(date)} ${hh}:${mi}`;
}

/** `2026-08` -> `Aug 26`. */
const MONTH_FORMATTER = new Intl.DateTimeFormat('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });

export function formatMonthKey(key: string): string {
  const [year, month] = key.split('-').map(Number);
  if (!year || !month) return key;
  return MONTH_FORMATTER.format(new Date(Date.UTC(year, month - 1, 1)));
}

export interface Countdown {
  text: string;
  days: number;
  tone: 'past' | 'soon' | 'later';
}

const DAY_MS = 86_400_000;

/** Whole days until `iso`, negative once it is past. */
export function daysUntil(iso: string | Date, now: Date = new Date()): number {
  const at = typeof iso === 'string' ? new Date(iso) : iso;
  return Math.floor((at.getTime() - now.getTime()) / DAY_MS);
}

export function formatCountdown(iso: string | Date, now: Date = new Date()): Countdown {
  const days = daysUntil(iso, now);
  if (days < 0) return { days, text: `${Math.abs(days)}d ago`, tone: 'past' };
  if (days === 0) return { days, text: 'today', tone: 'soon' };
  if (days <= 14) return { days, text: `in ${days}d`, tone: 'soon' };
  return { days, text: `in ${days}d`, tone: 'later' };
}

/** `10` + `MONTH` -> `10 / mo`; `275` + `YEAR` -> `275 / yr`; one-off -> `one-off`. */
export function formatRenewal(cost: number, currency: string, period: RenewalPeriod): string {
  if (period === 'NONE') return 'one-off';
  return `${formatAmount(cost, currency)} / ${period === 'MONTH' ? 'mo' : 'yr'}`;
}

/** `e@x.org, y@x.org` — clipped to `n` chips max, `+3` for the rest. */
export function clipList(values: string[], max: number): { visible: string[]; extra: number } {
  if (values.length <= max) return { visible: values, extra: 0 };
  return { visible: values.slice(0, max), extra: values.length - max };
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
